import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { accessRulesApi, peopleApi } from "../api/client.js";
import { useAuth } from "../auth/AuthContext.jsx";
import { useToast } from "../components/Toast.jsx";
import { BoardPager } from "../components/BoardPager.jsx";

/* Who may use this War Room.
 *
 * Two lists, in the order the questions are actually asked. First the
 * policy: which addresses are allowed to sign in at all — a whole domain
 * such as @insomniacs.in, or one named address. Somebody matching a rule
 * signs in with Google and is simply in; nobody has to type their address
 * first, which is the point of having rules rather than only names.
 *
 * Then the people: everyone who has a row, which is also the address book a
 * note can be composed to. A row is allowed or blocked, and blocking wins
 * over any rule — it is how one person is shut out without changing the
 * policy for their whole domain. */
export function AccessPage() {
  const { user: me } = useAuth();
  const toast = useToast();

  const [rules, setRules] = useState(null);
  const [users, setUsers] = useState(null);
  const [error, setError] = useState(null);
  const [ruleDraft, setRuleDraft] = useState(blankRule());
  const [addingRule, setAddingRule] = useState(false);
  const [droppingRule, setDroppingRule] = useState(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(blank());
  const [confirming, setConfirming] = useState(null);

  const [q, setQ] = useState("");
  const [filterTab, setFilterTab] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    let live = true;
    Promise.all([peopleApi.list(), accessRulesApi.list()])
      .then(([people, policy]) => {
        if (!live) return;
        setUsers(people.users);
        setRules(policy.rules);
      })
      .catch((e) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, []);

  async function addRule(e) {
    e.preventDefault();
    setAddingRule(true);
    try {
      const { rule } = await accessRulesApi.add({
        value: ruleDraft.value.trim(),
        role: ruleDraft.admin ? "admin" : "member",
        note: ruleDraft.note.trim(),
      });
      setRules((prev) => [...prev, rule].sort(byLabel));
      setRuleDraft(blankRule());
      toast(rule.label + " can now sign in");
    } catch (err) {
      toast(err.message, true);
    } finally {
      setAddingRule(false);
    }
  }

  async function patchRule(id, change, message) {
    try {
      const { rule } = await accessRulesApi.update(id, change);
      setRules((prev) => prev.map((r) => (r.id === id ? rule : r)).sort(byLabel));
      if (message) toast(message);
    } catch (err) {
      toast(err.message, true);
    }
  }

  async function removeRule(rule) {
    try {
      await accessRulesApi.remove(rule.id);
      setRules((prev) => prev.filter((r) => r.id !== rule.id));
      setDroppingRule(null);
      toast(rule.label + " removed — anyone already signed in through it keeps their access");
    } catch (err) {
      toast(err.message, true);
    }
  }

  async function add(e) {
    e.preventDefault();
    setAdding(true);
    try {
      const { user } = await peopleApi.add({
        email: draft.email.trim(),
        name: draft.name.trim(),
        title: draft.title.trim(),
        role: draft.admin ? "admin" : "member",
        daily: draft.daily,
        notes: draft.notes !== false,
        coreGroup: draft.coreGroup !== false,
      });
      setUsers((prev) => [...prev, user].sort(byName));
      setDraft(blank());
      toast(user.email + " can now sign in");
    } catch (err) {
      toast(err.message, true);
    } finally {
      setAdding(false);
    }
  }

  async function patch(id, change, message) {
    try {
      const { user } = await peopleApi.update(id, change);
      setUsers((prev) => prev.map((u) => (u.id === id ? user : u)).sort(byName));
      if (message) toast(message);
    } catch (err) {
      toast(err.message, true);
    }
  }

  async function remove(u) {
    try {
      await peopleApi.remove(u.id);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
      setConfirming(null);
      toast(u.email + " removed from the access list");
    } catch (err) {
      toast(err.message, true);
    }
  }

  const filteredUsers = useMemo(() => {
    if (!users) return [];
    const query = q.trim().toLowerCase();
    return users.filter((u) => {
      if (filterTab === "admin" && u.role !== "admin") return false;
      if (filterTab === "daily" && !u.daily) return false;
      if (filterTab === "notes" && u.notes === false) return false;
      if (filterTab === "core" && u.coreGroup === false) return false;
      if (filterTab === "blocked" && u.active) return false;

      if (!query) return true;
      return (
        (u.name || "").toLowerCase().includes(query) ||
        (u.email || "").toLowerCase().includes(query) ||
        (u.title || "").toLowerCase().includes(query) ||
        (u.joinedVia || "").toLowerCase().includes(query)
      );
    });
  }, [users, q, filterTab]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIdx = (currentPage - 1) * pageSize;
  const paginatedUsers = filteredUsers.slice(startIdx, startIdx + pageSize);
  const fromIdx = filteredUsers.length > 0 ? startIdx + 1 : 0;
  const toIdx = Math.min(startIdx + pageSize, filteredUsers.length);
  const isFiltered = Boolean(q.trim()) || filterTab !== "all";

  return (
    <div className="page">
      <div className="pagehead">
        <div>
          <h2 className="pagetitle">Access</h2>
          <p className="pagesub">
            Sign-in is Google only. An address gets in if a rule allows it or
            it is named below — everyone else is refused at the door.
          </p>
        </div>
        <Link className="btn btn-sm" to="/">
          Back to the board
        </Link>
      </div>

      {error && (
        <div className="notice">
          <b>Could not read the access list.</b> {error}
        </div>
      )}

      {/* ── the policy ─────────────────────────────────────────────── */}

      <div className="sechead">
        <span className="label">Allow an address or a whole domain</span>
        <span className="rule" />
      </div>

      <form className="addform" onSubmit={addRule}>
        <input
          className="in grow"
          required
          placeholder="@insomniacs.in  or  mail@insomniacs.in"
          aria-label="Domain or address to allow"
          value={ruleDraft.value}
          onChange={(e) => setRuleDraft({ ...ruleDraft, value: e.target.value })}
        />
        <input
          className="in grow"
          placeholder="Why (optional)"
          aria-label="Why this rule exists"
          value={ruleDraft.note}
          onChange={(e) => setRuleDraft({ ...ruleDraft, note: e.target.value })}
        />
        <label className="check">
          <input
            type="checkbox"
            checked={ruleDraft.admin}
            onChange={(e) => setRuleDraft({ ...ruleDraft, admin: e.target.checked })}
          />
          Joins as administrator
        </label>
        <button className="btn btn-sm btn-solid" type="submit" disabled={addingRule}>
          {addingRule ? "Allowing…" : "Allow"}
        </button>
      </form>

      <p className="hintline">
        Start with <span className="mono">@</span> for a whole domain —{" "}
        <span className="mono">@insomniacs.in</span> lets anyone with a Google
        account there sign in, and gives them a row the first time they do.
        Without the <span className="mono">@</span> it is that one address
        only, wherever it is hosted.
      </p>

      <div className="sechead" style={{ paddingTop: "20px" }}>
        <span className="label">Rules{rules ? " · " + rules.length : ""}</span>
        <span className="rule" />
      </div>

      {rules === null ? (
        <p className="empty">Reading the access rules…</p>
      ) : rules.length === 0 ? (
        <p className="empty">
          No rules. Only the people named below can sign in — add{" "}
          <span className="mono">@insomniacs.in</span> to let the company in.
        </p>
      ) : (
        <div className="rows">
          {rules.map((r) => (
            <div className={"arow" + (r.active ? "" : " off")} key={r.id}>
              <div className="acell who">
                <span className="mono rulelabel">{r.label}</span>
                <span className="sub">
                  {r.kind === "domain" ? "every address at this domain" : "this address only"}
                  {r.addedBy ? " · added by " + r.addedBy : ""}
                </span>
              </div>

              <div className="acell">
                <input
                  className="in"
                  value={r.note}
                  placeholder="Why this rule exists"
                  aria-label={"Why " + r.label + " is allowed"}
                  onChange={(e) =>
                    setRules((prev) =>
                      prev.map((x) => (x.id === r.id ? { ...x, note: e.target.value } : x))
                    )
                  }
                  onBlur={(e) => patchRule(r.id, { note: e.target.value })}
                />
              </div>

              <label className="check">
                <input
                  type="checkbox"
                  checked={r.role === "admin"}
                  onChange={(e) =>
                    patchRule(
                      r.id,
                      { role: e.target.checked ? "admin" : "member" },
                      e.target.checked
                        ? r.label + " now joins as administrator"
                        : r.label + " now joins as member"
                    )
                  }
                />
                Joins as admin
              </label>

              <span className={"tag " + (r.active ? "on" : "off")}>
                {r.active ? "allowed" : "off"}
              </span>

              <div className="acell acts">
                <button
                  className={"btn btn-sm" + (r.active ? "" : " btn-solid")}
                  type="button"
                  onClick={() =>
                    patchRule(
                      r.id,
                      { active: !r.active },
                      r.active
                        ? r.label + " can no longer sign in"
                        : r.label + " can sign in again"
                    )
                  }
                >
                  {r.active ? "Switch off" : "Switch on"}
                </button>
                {droppingRule === r.id ? (
                  <>
                    <button
                      className="btn btn-sm btn-armed"
                      type="button"
                      onClick={() => removeRule(r)}
                    >
                      Remove for good
                    </button>
                    <button
                      className="btn btn-sm"
                      type="button"
                      onClick={() => setDroppingRule(null)}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    className="btn btn-sm"
                    type="button"
                    onClick={() => setDroppingRule(r.id)}
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="hintline">
        Switching a rule off stops new people arriving through it. It does not
        touch anyone who already has a row — block them below for that.
      </p>

      {/* ── the people ─────────────────────────────────────────────── */}

      <div className="sechead" style={{ paddingTop: "28px" }}>
        <span className="label">Give one person access</span>
        <span className="rule" />
      </div>

      <form className="addform" onSubmit={add}>
        <input
          className="in grow"
          type="email"
          required
          placeholder="name@insomniacs.in"
          aria-label="Email address"
          value={draft.email}
          onChange={(e) => setDraft({ ...draft, email: e.target.value })}
        />
        <input
          className="in grow"
          placeholder="Full name"
          aria-label="Full name"
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
        />
        <input
          className="in grow"
          placeholder="Role in the org"
          aria-label="Role in the org"
          value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
        />
        <label className="check">
          <input
            type="checkbox"
            checked={draft.daily}
            onChange={(e) => setDraft({ ...draft, daily: e.target.checked })}
          />
          Daily reviewer
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={draft.notes !== false}
            onChange={(e) => setDraft({ ...draft, notes: e.target.checked })}
          />
          Notes recipient
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={draft.coreGroup !== false}
            onChange={(e) => setDraft({ ...draft, coreGroup: e.target.checked })}
          />
          Core group
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={draft.admin}
            onChange={(e) => setDraft({ ...draft, admin: e.target.checked })}
          />
          Administrator
        </label>
        <button className="btn btn-sm btn-solid" type="submit" disabled={adding}>
          {adding ? "Adding…" : "Add"}
        </button>
      </form>

      <p className="hintline">
        A row is a grant in its own right, so this is how an address outside
        every rule gets in. That person signs in with Google using this exact
        address.
      </p>

      <div className="sechead" style={{ paddingTop: "20px" }}>
        <span className="label">
          On the list{users ? " · " + users.length : ""}
        </span>
        <span className="rule" />
      </div>

      {users && users.length > 0 && (
        <div className="filterbar" style={{ marginTop: "12px", marginBottom: "12px" }}>
          <input
            className="in filterq"
            type="search"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            placeholder="Filter by name, email, or role…"
            aria-label="Filter access list by name, email, or role"
          />

          <div className="chipset" role="group" aria-label="Filter by attribute">
            {[
              { id: "all", label: "All" },
              { id: "admin", label: "Admin" },
              { id: "daily", label: "Daily" },
              { id: "notes", label: "Notes" },
              { id: "core", label: "Core group" },
              { id: "blocked", label: "Blocked" },
            ].map((item) => {
              const on = filterTab === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  className={"fchip" + (on ? " on" : "")}
                  aria-pressed={on}
                  onClick={() => {
                    setFilterTab(item.id);
                    setPage(1);
                  }}
                >
                  {item.label}
                </button>
              );
            })}
          </div>

          {isFiltered && (
            <button
              className="btn btn-sm"
              type="button"
              onClick={() => {
                setQ("");
                setFilterTab("all");
                setPage(1);
              }}
            >
              Clear filter
            </button>
          )}

          <div className="filterend">
            <span className="filtercount">
              {filteredUsers.length === users.length
                ? `${users.length} total`
                : `${filteredUsers.length} of ${users.length}`}
            </span>
            <select
              className="in"
              style={{ width: "auto", padding: "4px 8px", fontSize: "12px" }}
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              aria-label="Items per page"
            >
              <option value={10}>10 / page</option>
              <option value={20}>20 / page</option>
              <option value={50}>50 / page</option>
              <option value={100}>100 / page</option>
            </select>
          </div>
        </div>
      )}

      {users === null ? (
        <p className="empty">Reading the access list…</p>
      ) : filteredUsers.length === 0 ? (
        <p className="empty">
          {users.length === 0
            ? "Nobody is on the access list yet."
            : "No people match the filter."}
        </p>
      ) : (
        <>
          <div className="rows">
            {paginatedUsers.map((u) => {
              const isMe = me && u.id === me.id;
              return (
                <div className={"arow" + (u.active ? "" : " off")} key={u.id}>
                <div className="acell who">
                  <input
                    className="in"
                    value={u.name}
                    placeholder="Name"
                    aria-label={"Name of " + u.email}
                    onChange={(e) =>
                      setUsers((prev) =>
                        prev.map((x) => (x.id === u.id ? { ...x, name: e.target.value } : x))
                      )
                    }
                    onBlur={(e) => patch(u.id, { name: e.target.value })}
                  />
                  <span className="mono email">{u.email}</span>
                  {u.joinedVia && <span className="sub">joined via {u.joinedVia}</span>}
                </div>

                <div className="acell">
                  <input
                    className="in"
                    value={u.title}
                    placeholder="Role in the org"
                    aria-label={"Role of " + u.email}
                    onChange={(e) =>
                      setUsers((prev) =>
                        prev.map((x) => (x.id === u.id ? { ...x, title: e.target.value } : x))
                      )
                    }
                    onBlur={(e) => patch(u.id, { title: e.target.value })}
                  />
                </div>

                <label className="check">
                  <input
                    type="checkbox"
                    checked={u.daily}
                    onChange={(e) =>
                      patch(u.id, { daily: e.target.checked },
                        e.target.checked
                          ? u.email + " is now a daily reviewer"
                          : u.email + " is no longer a daily reviewer")
                    }
                  />
                  Daily
                </label>

                <label className="check">
                  <input
                    type="checkbox"
                    checked={u.notes !== false}
                    onChange={(e) =>
                      patch(u.id, { notes: e.target.checked },
                        e.target.checked
                          ? u.email + " can now receive notes"
                          : u.email + " hidden from Send a note")
                    }
                  />
                  Notes
                </label>

                <label className="check">
                  <input
                    type="checkbox"
                    checked={u.coreGroup !== false}
                    onChange={(e) =>
                      patch(u.id, { coreGroup: e.target.checked },
                        e.target.checked
                          ? u.email + " is now shown in the core group"
                          : u.email + " is now hidden from the core group")
                    }
                  />
                  Core group
                </label>

                <label className="check">
                  <input
                    type="checkbox"
                    checked={u.role === "admin"}
                    disabled={isMe}
                    title={isMe ? "Ask another administrator to change your own role" : undefined}
                    onChange={(e) =>
                      patch(u.id, { role: e.target.checked ? "admin" : "member" },
                        e.target.checked ? u.email + " is now an administrator" : u.email + " is now a member")
                    }
                  />
                  Admin
                </label>

                <span className={"tag " + (u.active ? "on" : "off")}>
                  {u.active ? "allowed" : "blocked"}
                </span>

                <div className="acell acts">
                  <button
                    className={"btn btn-sm" + (u.active ? "" : " btn-solid")}
                    type="button"
                    disabled={isMe}
                    title={isMe ? "You cannot remove your own access" : undefined}
                    onClick={() =>
                      patch(u.id, { active: !u.active },
                        u.active
                          ? u.email + " can no longer sign in"
                          : u.email + " can sign in again")
                    }
                  >
                    {u.active ? "Block" : "Allow"}
                  </button>
                  {confirming === u.id ? (
                    <>
                      <button className="btn btn-sm btn-armed" type="button" onClick={() => remove(u)}>
                        Remove for good
                      </button>
                      <button className="btn btn-sm" type="button" onClick={() => setConfirming(null)}>
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      className="btn btn-sm"
                      type="button"
                      disabled={isMe}
                      onClick={() => setConfirming(u.id)}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {totalPages > 1 && (
          <BoardPager
            page={currentPage}
            pages={totalPages}
            from={fromIdx}
            to={toIdx}
            total={filteredUsers.length}
            onPage={setPage}
          />
        )}
      </>
    )}

      <p className="hintline">
        Blocking takes effect on that person’s next request — it does not wait
        for their session to expire, and it holds even when a rule would have
        let them in. Removing deletes the row but keeps their acknowledgement
        history, which is keyed by address; if a rule still allows their
        address they can sign in again and get a fresh row, so block rather
        than remove to shut somebody out.
      </p>
    </div>
  );
}

const blank = () => ({ email: "", name: "", title: "", daily: false, admin: false, notes: true, coreGroup: true });
const blankRule = () => ({ value: "", note: "", admin: false });
const byName = (a, b) => (a.name || a.email).localeCompare(b.name || b.email);
const byLabel = (a, b) => a.label.localeCompare(b.label);
